export type BenefitItem = {
  benefitId: number;
  serviceSeqNo: string;
  serviceName: string;
  competentOrgName: string;
  applicationPeriod: string;
  applyFormat: string;
  applicationAvailabilityType:
    | 'DIRECT_GOV24'
    | 'DIRECT_ONLINE'
    | 'EXTERNAL_SITE'
    | 'VIEW_ONLY';
  introductionContent: string;
  isBookmarked: boolean;
  categoryCode: string;
  imageUrl: string | null;
  externalSiteUrl: string | null;
};

export type BenefitSearchResponse = {
  ok: boolean;
  data: {
    currentPage: number;
    totalPages: number;
    totalElements: number;
    benefits: BenefitItem[];
  };
};

function getApiBase(): string {
  const fromEnv = import.meta.env.VITE_GOV_BENEFIT_API_BASE as
    | string
    | undefined;
  if (fromEnv && fromEnv.trim()) return fromEnv.trim().replace(/\/$/, '');
  return 'https://web-gw.3o3.co.kr/gov-benefit';
}

export type BenefitFilter = {
  categoryCode?: string | null;
  region?: string | null;
  keyword?: string | null;
};

// sortOrder=01 => 최신등록순 (most recent upload first)
export async function fetchBenefitsLatest(
  page: number,
  filter: BenefitFilter = {},
  signal?: AbortSignal
): Promise<BenefitSearchResponse['data']> {
  const params = new URLSearchParams({
    page: String(page),
    sortOrder: '01',
  });
  if (filter.categoryCode) {
    params.set('serviceCategoryCode', filter.categoryCode);
  }
  if (filter.region) {
    params.set('region', filter.region);
  }
  if (filter.keyword) {
    params.set('keyword', filter.keyword);
  }
  const url = `${getApiBase()}/api/v1/gov/benefit/search?${params.toString()}`;
  const res = await fetch(url, { signal });
  if (!res.ok) {
    throw new Error(`Benefit search failed: ${res.status}`);
  }
  const json = (await res.json()) as BenefitSearchResponse;
  if (!json.ok || !json.data) {
    throw new Error('Benefit search returned no data');
  }
  return json.data;
}

export type BenefitLegalBasis = {
  lawInfoSequence: string;
  lawKoreanName: string;
  lawTypeName: string;
};

export type BenefitDetail = {
  sequenceNo: string;
  name: string;
  introduction: string;
  purpose: string;
  supportFormat: string;
  applicationDeadline: string;
  applicationProcessType: string;
  targetAudience: string;
  selectionCriteria: string;
  content: string;
  applicationProcess: string;
  requiredDocuments: string;
  receivingOrganization: string;
  referenceName: string;
  referencePhoneNo: string;
  jurisdictionOrganization: string;
  applicationAvailabilityType: BenefitItem['applicationAvailabilityType'];
  externalSiteUrl: string;
  modifiedDate: string;
  legalBases: BenefitLegalBasis[];
  categoryCode: string;
  imageUrl: string | null;
};

type BenefitDetailResponse = {
  ok: boolean;
  data: BenefitDetail;
};

export type BenefitCategory = {
  code: string;
  displayName: string;
};

type BenefitCategoriesResponse = {
  ok: boolean;
  data: { categories: BenefitCategory[] };
};

export async function fetchBenefitCategories(
  signal?: AbortSignal
): Promise<BenefitCategory[]> {
  const url = `${getApiBase()}/api/v1/gov/benefit/categories`;
  const res = await fetch(url, { signal });
  if (!res.ok) {
    throw new Error(`Categories fetch failed: ${res.status}`);
  }
  const json = (await res.json()) as BenefitCategoriesResponse;
  if (!json.ok || !json.data) {
    throw new Error('Categories returned no data');
  }
  return json.data.categories;
}

export async function fetchBenefitDetail(
  serviceSeqNo: string,
  signal?: AbortSignal
): Promise<BenefitDetail> {
  const url = `${getApiBase()}/api/v1/gov/benefit/details/${encodeURIComponent(serviceSeqNo)}`;
  const res = await fetch(url, { signal });
  if (!res.ok) {
    throw new Error(`Benefit detail failed: ${res.status}`);
  }
  const json = (await res.json()) as BenefitDetailResponse;
  if (!json.ok || !json.data) {
    throw new Error('Benefit detail returned no data');
  }
  return json.data;
}
